import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc, Id } from '@goaltree/backend/convex/_generated/dataModel'
import { isFast } from '@goaltree/backend/convex/lib/habitMeasure'
import { scheduleDays, scheduleFromDays } from '@goaltree/backend/convex/lib/schedule'
import { habitViewing, taskViewing } from '@goaltree/backend/convex/lib/viewingContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Calendar } from '@goaltree/ui/components/calendar'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@goaltree/ui/components/popover'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { enUS, es, fr, ja, pt, zhCN } from '@goaltree/ui/lib/locales'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useAction, useMutation, useQuery } from 'convex/react'
import i18next from 'i18next'
import {
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  Camera,
  ChevronRight,
  Clock,
  Flame,
  FolderInput,
  History,
  Loader2,
  Pin,
  PinOff,
  Play,
  Plus,
  RefreshCw,
  Share2,
  Square,
  Sun,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  AskCoachComposer,
  AskCoachThread,
  useAskChips,
  useCoachPersona,
} from '@/components/ask-coach'
import { AutoCheckRow } from '@/components/auto-check-row'
import { ConvertToEventDialog } from '@/components/convert-to-event'
import {
  DetailActions,
  DetailKindLabel,
  DetailMenu,
  type DetailMenuItem,
  DetailPrimaryAction,
  DetailRow,
  DetailRows,
} from '@/components/detail-panel'
import { PhotoCheckinSheet, useHabitPhotos } from '@/components/dot-photo'
import { GoalPickerSheet } from '@/components/goal-picker-sheet'
import { DotStyleButton } from '@/components/habit-dots'
import { HabitIcon, HabitIconPicker, useHabitIconName } from '@/components/habit-icon'
import { HabitAnchorPicker, useAnchorText, useLiveHabit } from '@/components/habit-stack'
import { HabitStrengthPanel, StrengthChip, useHabitStrength } from '@/components/habit-strength'
import { HabitTimePicker } from '@/components/habit-time-picker'
import { HabitDotGrid, habitColor, habitStats, inCurrentPeriod } from '@/components/inbox-habits'
import { LevelUpSection } from '@/components/level-up'
import { MarkdownContent } from '@/components/markdown-content'
import {
  MeasureSettings,
  NumberHabitStrip,
  useFastActions,
  useHabitMeasure,
} from '@/components/number-habit'
import { QuickLogLinkRow } from '@/components/quick-log-link'
import { QuitHero, QuitToggleRow } from '@/components/quit-habit'
import { RecursDaysChips } from '@/components/recurs-days-chips'
import { SavedHint, useSavedHint } from '@/components/saved-hint'
import { SessionPlanSection } from '@/components/session-plan'
import { openHabitShare } from '@/components/share-card'
import { HABIT_SKIP_REASONS, SkipReasonIcon } from '@/components/skip-reason-toast'
import { StreakFlame } from '@/components/streak-icons'
import { TaskLocationRow } from '@/components/task-location'
import { HabitStatsLine, UrgentFlag } from '@/components/task-meta'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useBreather } from '@/hooks/use-breather'
import { useIsPro } from '@/hooks/use-is-pro'
import {
  doneOnDay,
  dueOn,
  useMarkYesterday,
  yesterdayDate,
  yesterdayKey,
} from '@/hooks/use-mark-yesterday'
import { type MustDoDay, useMustDo } from '@/hooks/use-must-do'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { useSetTaskStatus } from '@/hooks/use-set-task-status'
import { useStickToBottom } from '@/hooks/use-stick-to-bottom'
import { useTaskExplain } from '@/hooks/use-task-explain'
import { localDateStr } from '@/hooks/use-today-tasks'
import { caretOffsetFromPoint } from '@/lib/caret'
import { categoryTextColor, useCategoryFill } from '@/lib/category-color'
import { useReportViewing } from '@/lib/coach-call-store'
import { isTooFarBack, logicalDayStr, todayKey } from '@/lib/day'
import {
  optimisticAllDay,
  optimisticDueDate,
  optimisticDuration,
  optimisticRecurs,
  patchNode,
} from '@/lib/nodes-optimistic'
import { POPULAR_HABIT_CATEGORIES } from '@/lib/popular-habits'
import { periodLabel } from '@/lib/schedule'
import { useSessionPlanUi } from '@/lib/session-plan-store'
import { playDoneSound } from '@/lib/sounds'
import {
  addUrgentToTitle,
  parseTaskFields,
  removeUrgentFromTitle,
  taskDescription,
  taskTags,
  taskTitle,
} from '@/lib/tags'
import {
  coerceEnd,
  isDueTodayOrOverdue,
  isTaskOverdue,
  nextHourEnd,
  nextHourStart,
  recurrenceOverdue,
  recursOnToday,
  shiftTime,
  toMinutes,
} from '@/lib/time'
import { openTimePicker } from '@/lib/time-input'
import { handleUsageLimit } from '@/lib/usage-limit'
import { goalUrl } from '@/lib/utils'
import { useOfflineStore } from '@/store/offline.store'

const DATE_FNS_LOCALES: Record<string, typeof enUS> = { en: enUS, es, fr, ja, pt, zh: zhCN }
const RECURS_PERIODS = [
  'daily',
  'everyOtherDay',
  'weekly',
  'monthly',
  'weekend',
  'weekday',
  'days',
] as const
type RecursPeriod = (typeof RECURS_PERIODS)[number]
// Seeded when "Specific days" is picked for a task that doesn't repeat yet.
const DEFAULT_DAYS = [1, 3, 5]

export type InboxTaskForSheet = {
  _id: Id<'nodes'>
  mustDoOn?: string
  _creationTime?: number
  title: string
  description?: string
  tags?: string[]
  effort: 'low' | 'medium' | 'high'
  superUrgent?: boolean
  completedAt: number | null
  recursPeriod?: string
  recursDays?: number[] | null
  recurs?: boolean
  recurrenceTime?: string
  recurrenceDay?: number
  dueDate?: number
  durationMinutes?: number
  allDay?: boolean
  status?: 'backlog' | 'in-progress' | 'done'
  parentId?: Id<'nodes'> | null
  isHabit?: boolean
  pinnedAt?: number
  category?: string
  icon?: string | null
  anchorHabitId?: Id<'nodes'> | null
  anchorMoment?: string | null
  location?: string
}

/** Rebuild the editable `title #tags /// description /// urgent` string from structured fields. */
function editableTaskString(t: {
  title: string
  description?: string | null
  tags?: string[] | null
  effort?: 'low' | 'medium' | 'high'
  superUrgent?: boolean
}): string {
  const tagStr = taskTags(t)
    .map((tag) => `#${tag}`)
    .join(' ')
  let s = tagStr ? `${taskTitle(t)} ${tagStr}` : taskTitle(t)
  const desc = taskDescription(t)
  if (desc) s += ` /// ${desc}`
  if (t.effort === 'high') s += t.superUrgent ? ' /// super urgent' : ' /// urgent'
  return s
}

export function toSheetTask(n: Doc<'nodes'>): InboxTaskForSheet {
  return {
    _id: n._id,
    _creationTime: n._creationTime,
    title: n.title,
    description: n.description,
    tags: n.tags,
    effort: n.effort,
    superUrgent: n.superUrgent,
    completedAt: n.completedAt ?? null,
    dueDate: n.dueDate,
    durationMinutes: n.durationMinutes,
    allDay: n.allDay,
    status: n.status,
    parentId: n.parentId ?? null,
    category: n.category,
    location: n.location,
    mustDoOn: n.mustDoOn,
    pinnedAt: n.pinnedAt,
  }
}

type Props = {
  task: InboxTaskForSheet | null
  onClose: () => void
  onComplete: (taskId: Id<'nodes'>) => void
  onUncomplete: (taskId: Id<'nodes'>) => void
  // Set when opened from a calendar occurrence; drives per-day completion.
  occurrenceDay?: Date | null
  // "Not today" — hides a habit from Today for the day (Today screen only, never
  // required). Optional reason. Absent in the inbox, where skip doesn't apply.
  onSkipToday?: (habitId: Id<'nodes'>, reason?: string) => void
  // Pin / unpin to the top of the inbox tab. Absent outside the inbox.
  onTogglePin?: (taskId: Id<'nodes'>) => void
}

function occLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function InboxTaskDetailSheet({
  task: taskProp,
  onClose: closeSheet,
  onComplete,
  onUncomplete,
  occurrenceDay,
  onSkipToday,
  onTogglePin,
}: Props) {
  const { t, i18n } = useTranslation()
  const fill = useCategoryFill()
  const navigate = useNavigate()
  const sheetStrength = useHabitStrength(taskProp?._id)
  const task = taskProp
  const onClose = closeSheet
  // Back from the stats page returns to the list, not to a reopened sheet.
  const openStats = () => {
    if (!task) return
    onClose()
    navigate({ to: '/habits/$habitId', params: { habitId: task._id as string } })
  }
  const { isPro } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const chat = useTaskExplain()
  const { ask, isLoading, streamedText, isStreaming, reset } = chat
  const askChips = useAskChips('task')
  const { voice: coachVoice, name: coachName } = useCoachPersona(!!task)
  const responseRef = useRef<HTMLDivElement>(null)

  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [localTitle, setLocalTitle] = useState('')
  const [localFullTitle, setLocalFullTitle] = useState('')
  const titleHint = useSavedHint()
  const renameRef = useRef<HTMLTextAreaElement>(null)
  const tapPositionRef = useRef<{ x: number; y: number } | null>(null)
  const renameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [locallyDone, setLocallyDone] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [isUrgent, setIsUrgent] = useState(false)
  const [isSuper, setIsSuper] = useState(false)
  const [localDueDate, setLocalDueDate] = useState<number | undefined>()
  const [localRecursPeriod, setLocalRecursPeriod] = useState<RecursPeriod | null>(null)
  const [localRecurrenceDay, setLocalRecurrenceDay] = useState<number>(1)
  const [localDays, setLocalDays] = useState<number[]>([])
  const [daysOpen, setDaysOpen] = useState(false)
  const [localHabit, setLocalHabit] = useState(false)
  const { onBreather } = useBreather()
  const sessionFull = useSessionPlanUi((s) => !!taskProp && s.fullscreen === taskProp._id)
  const setSessionFull = useSessionPlanUi((s) => s.setFullscreen)
  const [localCategory, setLocalCategory] = useState<string | undefined>()
  const [localIcon, setLocalIcon] = useState<string | undefined>()
  const [localAnchor, setLocalAnchor] = useState<{
    anchorHabitId?: Id<'nodes'> | null
    anchorMoment?: string | null
  }>({})
  // Optimistic pin state — `task` is a snapshot, so reflect toggles immediately.
  const [localPinned, setLocalPinned] = useState(false)

  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [recursOpen, setRecursOpen] = useState(false)
  const [linkingToGoal, setLinkingToGoal] = useState(false)
  const [pendingPromote, setPendingPromote] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [pendingUnhabit, setPendingUnhabit] = useState(false)
  const [promoting, setPromoting] = useState(false)

  const [timeStr, setTimeStr] = useState('09:00')
  const [recurrenceTimeStr, setRecurrenceTimeStr] = useState('09:00')
  const [recurrenceEndTimeStr, setRecurrenceEndTimeStr] = useState('10:00')
  const [selectedDay, setSelectedDay] = useState<Date | undefined>()
  // End-time for one-off (dated) events — start is the dueDate time, end is
  // derived from durationMinutes. Recurring tasks use recurrenceEndTimeStr.
  const [dueEndTimeStr, setDueEndTimeStr] = useState('10:00')
  // Optimistic all-day flag — reflects the toggle before the server round-trips.
  const [localAllDay, setLocalAllDay] = useState(false)
  // Stable matcher so react-day-picker doesn't churn cells (flicker + swallowed
  // clicks) when the surrounding screen re-renders with the popover open.
  const dueDisabled = useMemo(() => ({ before: new Date(new Date().setHours(0, 0, 0, 0)) }), [])

  const renameFull = useMutation(api.nodes.rename).withOptimisticUpdate((localStore, args) => {
    if (!task?.parentId) return
    const existing = localStore.getQuery(api.nodes.getByParent, {
      parentId: task.parentId,
    })
    if (!existing) return
    localStore.setQuery(
      api.nodes.getByParent,
      { parentId: task.parentId },
      existing.map((n) =>
        n._id === args.nodeId
          ? {
              ...n,
              title: args.title,
              description: args.description ?? undefined,
              tags: args.tags ?? [],
            }
          : n,
      ),
    )
  })
  const setTaskStatus = useSetTaskStatus()
  const updateEffort = useMutation(api.nodes.updateTaskEffort).withOptimisticUpdate(
    (localStore, args) => {
      if (!task?.parentId) return
      const tasks = localStore.getQuery(api.nodes.getByParent, { parentId: task.parentId })
      if (!tasks) return
      localStore.setQuery(
        api.nodes.getByParent,
        { parentId: task.parentId },
        tasks.map((n) =>
          n._id === args.taskId ? { ...n, effort: args.effort, superUrgent: args.superUrgent } : n,
        ),
      )
    },
  )
  const updateDueDate = useMutation(api.nodes.updateTaskDueDate).withOptimisticUpdate(
    optimisticDueDate,
  )
  const updateRecursPeriod = useMutation(api.nodes.updateRecursPeriod).withOptimisticUpdate(
    optimisticRecurs,
  )
  const updateTaskDuration = useMutation(api.nodes.updateTaskDuration).withOptimisticUpdate(
    optimisticDuration,
  )
  const setTaskAllDay = useMutation(api.nodes.setTaskAllDay).withOptimisticUpdate(optimisticAllDay)
  const updateCategory = useMutation(api.nodes.updateTaskCategory).withOptimisticUpdate(
    (localStore, { taskId, category }) => patchNode(localStore, taskId, { category }),
  )
  const updateIcon = useMutation(api.nodes.updateHabitIcon).withOptimisticUpdate(
    (localStore, { taskId, icon }) => patchNode(localStore, taskId, { icon: icon ?? null }),
  )
  const online = useOnlineStatus()
  const enqueueOp = useOfflineStore((s) => s.enqueue)
  // Route effort through the offline queue when disconnected so the urgent dot
  // reflects on the list immediately and survives reloads (parity with online).
  const applyEffort = (effort: 'low' | 'medium' | 'high', superUrgent = false) => {
    if (!task) return
    if (online) updateEffort({ taskId: task._id, effort, superUrgent }).catch(() => {})
    else enqueueOp({ kind: 'setEffort', args: { taskId: task._id as string, effort, superUrgent } })
  }
  const setIsHabit = useMutation(api.nodes.setIsHabit).withOptimisticUpdate(
    (localStore, { taskId, isHabit }) => {
      const parentId = task?.parentId
      if (!parentId) return
      const tasks = localStore.getQuery(api.nodes.getByParent, { parentId })
      if (!tasks) return
      localStore.setQuery(
        api.nodes.getByParent,
        { parentId },
        tasks.map((t) =>
          t._id === taskId
            ? {
                ...t,
                isHabit,
                // Tracking a non-recurring task as a habit makes it a daily 09:00 habit.
                ...(isHabit && !(t.recurs || t.recursPeriod)
                  ? {
                      recurs: true,
                      recursPeriod: 'daily',
                      recurrenceTime: t.recurrenceTime ?? '09:00',
                      dueDate: undefined,
                    }
                  : {}),
              }
            : t,
        ),
      )
    },
  )
  const mustDo = useMustDo()
  const markYesterday = useMarkYesterday()
  const fastProgress = useHabitMeasure(task?._id)
  const fastActions = useFastActions()
  const isFastHabit = isFast(fastProgress?.measure)
  // The sheet holds a snapshot of the task, so track the Must do day locally.
  const [mustLocal, setMustLocal] = useState<{ id: string; day: MustDoDay | null } | null>(null)
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate(
    (localStore, { nodeId }) => {
      // Remove it from whichever lists are live so confirming delete is instant:
      // the inbox/goal children list, the habits list, and the Today surface.
      const parentId = task?.parentId
      if (parentId) {
        const kids = localStore.getQuery(api.nodes.getByParent, { parentId })
        if (kids) {
          localStore.setQuery(
            api.nodes.getByParent,
            { parentId },
            kids.filter((n) => n._id !== nodeId),
          )
        }
      }
      const habitList = localStore.getQuery(api.nodes.getHabits)
      if (habitList) {
        localStore.setQuery(
          api.nodes.getHabits,
          {},
          habitList.filter((h) => h._id !== nodeId),
        )
      }
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          { ...ctx, tasks: ctx.tasks.filter((t) => t.taskId !== nodeId) },
        )
      }
    },
  )
  const promoteInboxTaskToGoal = useAction(api.voice.promoteInboxTaskToGoal)
  const habits = useQuery(api.nodes.getHabits)
  const habitEntry0 = habits?.find((h) => h._id === task?._id)
  const habitCompletions = habitEntry0?.completions ?? []
  const habitPhotos = useHabitPhotos(task?._id, !!habitEntry0)
  const [photoOpen, setPhotoOpen] = useState(false)
  const setCompletionForDate = useMutation(api.nodes.setCompletionForDate).withOptimisticUpdate(
    (localStore, { taskId, date, done }) => {
      const rows = localStore.getQuery(api.nodes.getRecurringCompletionDates)
      if (!rows) return
      localStore.setQuery(
        api.nodes.getRecurringCompletionDates,
        {},
        rows.map((r) =>
          (r.taskId as string) === (taskId as string)
            ? {
                ...r,
                dates: done
                  ? r.dates.includes(date)
                    ? r.dates
                    : [...r.dates, date]
                  : r.dates.filter((d) => d !== date),
              }
            : r,
        ),
      )
    },
  )
  const recurCompletions = useQuery(api.nodes.getRecurringCompletionDates)
  // Per-day completion when opened from a calendar occurrence.
  const occ = useMemo(() => {
    if (!task || !occurrenceDay) return null
    if (!(task.recursPeriod || task.recurs)) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const d = new Date(occurrenceDay)
    d.setHours(0, 0, 0, 0)
    const kind =
      d.getTime() > today.getTime() ? 'future' : d.getTime() < today.getTime() ? 'past' : 'today'
    const ds = occLocalDateStr(d)
    const done = new Set(recurCompletions?.find((r) => r.taskId === task._id)?.dates ?? []).has(ds)
    const label = d.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
    return { kind, ds, done, label } as const
  }, [task, occurrenceDay, recurCompletions, i18n.language])

  useEffect(() => {
    if (renameTimerRef.current) {
      clearTimeout(renameTimerRef.current)
      renameTimerRef.current = null
    }
    if (task?.title) {
      setLocalFullTitle(editableTaskString(task))
      setLocalTitle(taskTitle(task))
    }
    reset()
    setRenaming(false)
    setPendingPromote(false)
    setPromoting(false)
    // A habit's completedAt can linger from a previous period; only this one counts.
    setLocallyDone(
      task?.completedAt != null &&
        (!task.isHabit || inCurrentPeriod(task.completedAt, task.recursPeriod)),
    )
    setInProgress(task?.status === 'in-progress')
    setIsUrgent(task?.effort === 'high')
    setIsSuper(!!task?.superUrgent)
    const hasRecurring = !!(task?.recursPeriod || task?.recurs)
    // Fold a legacy recurs:true (no explicit period) into 'daily' so localRecursPeriod
    // is the single source of truth for whether the task recurs — the render no
    // longer has to OR with the server `task.recurs`, which lags behind edits and
    // caused the due + recurring chips to briefly show together when switching.
    setLocalRecursPeriod(
      (task?.recursPeriod as RecursPeriod | undefined) ?? (task?.recurs ? 'daily' : null),
    )
    setLocalRecurrenceDay(task?.recurrenceDay ?? 1)
    setLocalDays(scheduleDays(task?.recursPeriod, task?.recurrenceDay, task?.recursDays))
    setDaysOpen(false)
    setLocalHabit(!!task?.isHabit)
    setLocalCategory(task?.category)
    setLocalPinned(task?.pinnedAt != null)
    setLocalAllDay(!!task?.allDay)
    setLocalDueDate(hasRecurring ? undefined : task?.dueDate)
    if (!hasRecurring && task?.dueDate) {
      const d = new Date(task.dueDate)
      setSelectedDay(d)
      const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      setTimeStr(start)
      const endMins = d.getHours() * 60 + d.getMinutes() + (task?.durationMinutes ?? 60)
      setDueEndTimeStr(
        `${String(Math.floor(endMins / 60) % 24).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
      )
    } else {
      setSelectedDay(undefined)
      setTimeStr('09:00')
      setDueEndTimeStr('10:00')
    }
    const startTime = task?.recurrenceTime || '09:00'
    setRecurrenceTimeStr(startTime)
    const [sh = 9, sm = 0] = startTime.split(':').map(Number)
    const endMins = sh * 60 + sm + (task?.durationMinutes ?? 60)
    setRecurrenceEndTimeStr(
      `${String(Math.floor(endMins / 60) % 24).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
    )
  }, [task?._id])

  const onResponseScroll = useStickToBottom(responseRef, streamedText, chat.pendingQuestion)

  useEffect(() => {
    const el = renameRef.current
    if (!el) return
    const size = () => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
    size()
    // Re-measure after layout settles — on open (and again once the keyboard's
    // viewport resize lands) the first measurement can read 0 and collapse it.
    requestAnimationFrame(size)
    const t = setTimeout(size, 120)
    // The sheet can still be settling its width; re-measure when it does.
    const ro = new ResizeObserver(size)
    ro.observe(el.parentElement ?? el)
    el.focus()
    const pos = tapPositionRef.current
    if (pos) {
      tapPositionRef.current = null
      const offset = caretOffsetFromPoint(el, pos.x, pos.y)
      el.setSelectionRange(offset, offset)
    } else {
      el.setSelectionRange(el.value.length, el.value.length)
    }
    return () => {
      clearTimeout(t)
      ro.disconnect()
    }
  }, [renaming])

  function handleAsk(text: string) {
    if (!task || !text.trim() || isLoading || isStreaming) return
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    ask(task.title, t('inbox.title'), text, task._id)
  }

  function clearRecurring() {
    setLocalRecursPeriod(null)
    updateRecursPeriod({
      taskId: task!._id,
      recursPeriod: undefined,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }

  function applyHabit(next: boolean) {
    if (!task) return
    setLocalHabit(next)
    // Tracking a non-recurring task as a habit makes it a daily 09:00 habit.
    const makesRecurring = next && !localRecursPeriod && !task.recurs
    if (makesRecurring) {
      setLocalRecursPeriod('daily')
      setRecurrenceTimeStr(task.recurrenceTime ?? '09:00')
      setLocalDueDate(undefined)
    }
    if (online) {
      setIsHabit({
        taskId: task._id,
        isHabit: next,
        ...(makesRecurring && { recurrenceTzOffset: new Date().getTimezoneOffset() }),
      }).catch((err) => {
        setLocalHabit(!next)
        handleUsageLimit(err)
      })
    } else {
      enqueueOp({ kind: 'setIsHabit', args: { taskId: task._id as string, isHabit: next } })
    }
  }

  function handleDaySelect(date: Date | undefined) {
    if (!task) return
    const target = date ?? selectedDay
    if (!target) return
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    const d = new Date(target)
    d.setHours(h, m, 0, 0)
    setSelectedDay(d)
    setLocalDueDate(d.getTime())
    updateDueDate({ taskId: task._id, dueDate: d.getTime() }).catch(() => {})
    if (localRecursPeriod) clearRecurring()
    setDueDateOpen(false)
  }

  function handleTimeChange(newTime: string) {
    if (!task) return
    setTimeStr(newTime)
    if (selectedDay) {
      const [h = 9, m = 0] = newTime.split(':').map(Number)
      const d = new Date(selectedDay)
      d.setHours(h, m, 0, 0)
      setLocalDueDate(d.getTime())
      updateDueDate({ taskId: task._id, dueDate: d.getTime() }).catch(() => {})
    }
  }

  function toggleAllDay() {
    if (!task) return
    const next = !localAllDay
    setLocalAllDay(next)
    if (next) {
      setTaskAllDay({ taskId: task._id, allDay: true }).catch(() => setLocalAllDay(false))
      return
    }
    // Switching to timed → set the next-hour range BEFORE clearing all-day, so the
    // list row never flashes as "timed at 09:00" (which reads as overdue).
    const start = nextHourStart()
    const end = nextHourEnd()
    if (localRecursPeriod) {
      setRecurrenceTimeStr(start)
      setRecurrenceEndTimeStr(end)
      updateRecursPeriod({
        taskId: task._id,
        recursPeriod: localRecursPeriod,
        recurrenceTime: start,
        recurrenceTzOffset: new Date().getTimezoneOffset(),
      }).catch(() => {})
      updateTaskDuration({ taskId: task._id, durationMinutes: 60 }).catch(() => {})
    } else {
      setDueEndTimeStr(end)
      handleTimeChange(start)
      updateTaskDuration({ taskId: task._id, durationMinutes: 60 }).catch(() => {})
    }
    setTaskAllDay({ taskId: task._id, allDay: false }).catch(() => setLocalAllDay(true))
  }

  // One-off (dated) event time edits. Start rewrites the dueDate time (keeping
  // the day) and keeps the end after it; end rewrites durationMinutes.
  function handleDueStartChange(v: string) {
    if (!task) return
    const newStart = v || '09:00'
    handleTimeChange(newStart)
    const startMin = toMinutes(newStart)
    const end =
      dueEndTimeStr && toMinutes(dueEndTimeStr) > startMin ? dueEndTimeStr : shiftTime(newStart, 15)
    setDueEndTimeStr(end)
    updateTaskDuration({
      taskId: task._id,
      durationMinutes: Math.max(15, toMinutes(end) - startMin),
    }).catch(() => {})
  }
  function handleDueEndChange(v: string) {
    if (!task) return
    const startStr = timeStr || '09:00'
    const newEnd = coerceEnd(startStr, v)
    setDueEndTimeStr(newEnd)
    updateTaskDuration({
      taskId: task._id,
      durationMinutes: Math.max(15, toMinutes(newEnd) - toMinutes(startStr)),
    }).catch(() => {})
  }

  function handleDatePreset(preset: 'today' | 'tomorrow' | 'thisWeekend' | 'nextWeek') {
    if (!task) return
    const d = new Date()
    switch (preset) {
      case 'tomorrow':
        d.setDate(d.getDate() + 1)
        break
      case 'thisWeekend': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 6 ? 0 : 6 - day))
        break
      }
      case 'nextWeek': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day))
        break
      }
    }
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    d.setHours(h, m, 0, 0)
    setSelectedDay(d)
    setLocalDueDate(d.getTime())
    updateDueDate({ taskId: task._id, dueDate: d.getTime() }).catch(() => {})
    if (localRecursPeriod) clearRecurring()
    setDueDateOpen(false)
  }

  const locale =
    DATE_FNS_LOCALES[i18next.language.split('-')[0] as keyof typeof DATE_FNS_LOCALES] ?? enUS
  const isDone = locallyDone
  // For a specific occurrence (calendar view), the title's completed styling
  // reflects that day's completion — not the recurring task's global completedAt,
  // which may be set from another day.
  const titleDone = occ ? occ.done : isDone
  // Today's recurring occurrence is overdue: due today, not done, past its
  // recurs time + duration. Drives the red styling on the recurrence chip.
  const recurrencePeriod = localRecursPeriod ?? undefined
  // Today's occurrence is overdue only if it isn't done *today* — a habit's
  // global completedAt can linger from a previous day, so gate on the per-day
  // completion log (matching the task list) rather than `isDone`. A timeless
  // habit falls back to a 9am effective time, matching the Today/inbox lists.
  // Only habits track daily occurrences, so only they can be recurrence-overdue;
  // a plain recurring task just shows its cadence label (matching the list).
  const doneToday = habitCompletions.some((ms) => logicalDayStr(ms) === logicalDayStr(Date.now()))
  const recurrenceIsOverdue =
    !onBreather &&
    localHabit &&
    !occ &&
    !doneToday &&
    !localAllDay &&
    !!recurrencePeriod &&
    recursOnToday(recurrencePeriod, localRecurrenceDay, localDays) &&
    // Use the live local time (reflects an all-day→timed switch to the next hour),
    // not the stale task prop — otherwise a just-set future time reads as overdue.
    recurrenceOverdue(
      recurrenceTimeStr,
      Math.max(15, toMinutes(recurrenceEndTimeStr) - toMinutes(recurrenceTimeStr)),
    )

  const hasCategory = !!localCategory && localCategory !== 'inbox'
  const liveHabit = useLiveHabit(task?._id)
  // Icon + cue come from the live habit list: Today hands the sheet a trimmed task.
  useEffect(() => {
    setLocalIcon(liveHabit?.icon ?? task?.icon ?? undefined)
    setLocalAnchor({
      anchorHabitId: liveHabit?.anchorHabitId ?? task?.anchorHabitId,
      anchorMoment: liveHabit?.anchorMoment ?? task?.anchorMoment,
    })
  }, [
    task?._id,
    task?.icon,
    task?.anchorHabitId,
    task?.anchorMoment,
    liveHabit?.icon,
    liveHabit?.anchorHabitId,
    liveHabit?.anchorMoment,
  ])
  const anchorLabel = useAnchorText(localAnchor)
  const iconName = useHabitIconName({
    icon: localIcon,
    title: localTitle,
    category: localCategory,
  })
  const habitEntry = habits?.find((h) => h._id === task?._id)
  const createdAt = task?._creationTime ?? habitEntry?._creationTime
  const stats = useMemo(
    () =>
      localHabit && habitEntry
        ? habitStats(
            habitEntry.completions,
            recurrencePeriod ?? habitEntry.recursPeriod,
            createdAt,
            localDays,
          )
        : null,
    [localHabit, habitEntry, recurrencePeriod, createdAt, localDays],
  )
  // During a live call the coach hears what this sheet shows (lib/viewingContext).
  useReportViewing(() => {
    if (!task) return null
    if (localHabit)
      return habitViewing(
        task._id,
        {
          title: task.title,
          period: recurrencePeriod ?? task.recursPeriod,
          recursDays: localDays,
          streak: stats?.now ?? null,
          strength: sheetStrength?.strength ?? null,
          doneToday,
          quit: habitEntry?.quit,
        },
        'sheet',
      )
    return taskViewing(task._id, {
      title: task.title,
      note: task.description,
      dueDate: task.dueDate,
      status: isDone ? 'done' : (task.status ?? 'backlog'),
    })
  })
  const taskTagList = task ? parseTaskFields(localFullTitle || task.title).tags : []

  function toggleComplete() {
    if (!task) return
    if (isDone) {
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current)
        completeTimerRef.current = null
      } else {
        onUncomplete(task._id)
      }
      setLocallyDone(false)
      return
    }
    setLocallyDone(true)
    completeTimerRef.current = setTimeout(() => {
      onComplete(task._id)
      completeTimerRef.current = null
    }, 600)
  }

  // On a done item this undoes completion and moves it to in-progress.
  function toggleInProgress() {
    if (!task) return
    if (isDone) {
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current)
        completeTimerRef.current = null
      } else {
        onUncomplete(task._id)
      }
      setLocallyDone(false)
      setInProgress(true)
      setTaskStatus({ taskId: task._id, status: 'in-progress' }).catch(() => setInProgress(false))
      return
    }
    const next = inProgress ? 'backlog' : 'in-progress'
    setInProgress(!inProgress)
    setTaskStatus({ taskId: task._id, status: next }).catch(() => setInProgress(inProgress))
  }

  function toggleUrgent() {
    if (!task) return
    // normal -> urgent -> super urgent -> normal
    const nextUrgent = !isUrgent || !isSuper
    const nextSuper = isUrgent && !isSuper
    setIsUrgent(nextUrgent)
    setIsSuper(nextSuper)
    const currentFull = removeUrgentFromTitle(localFullTitle || task.title)
    const newFull = nextUrgent ? addUrgentToTitle(currentFull, nextSuper) : currentFull
    setLocalFullTitle(newFull)
    if (renaming) setRenameValue(newFull)
    const { title, description, tags } = parseTaskFields(newFull)
    renameFull({ nodeId: task._id, title, description, tags }).catch(() => {})
    applyEffort(nextUrgent ? 'high' : 'low', nextSuper)
  }

  function setRecurTime(start: string, durationMinutes: number) {
    if (!task) return
    setRecurrenceTimeStr(start)
    setRecurrenceEndTimeStr(shiftTime(start, durationMinutes))
    updateRecursPeriod({
      taskId: task._id,
      recursPeriod: localRecursPeriod ?? undefined,
      recurrenceTime: start,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
    updateTaskDuration({ taskId: task._id, durationMinutes }).catch(() => {})
    // Cleared after the time is written so the row never reads as "timed at 09:00".
    if (localAllDay) {
      setLocalAllDay(false)
      setTaskAllDay({ taskId: task._id, allDay: false }).catch(() => setLocalAllDay(true))
    }
  }

  function setRecurAllDay() {
    if (!task || localAllDay) return
    setLocalAllDay(true)
    setTaskAllDay({ taskId: task._id, allDay: true }).catch(() => setLocalAllDay(false))
  }

  function recursDaySuffix() {
    if (localRecursPeriod === 'weekly')
      return (
        <span className="text-muted-foreground">
          {' · '}
          {new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(
            new Date(2025, 0, 5 + localRecurrenceDay),
          )}
        </span>
      )
    if (localRecursPeriod !== 'monthly') return null
    let day: string | number = localRecurrenceDay
    if (i18n.language.startsWith('en')) {
      try {
        const r = new Intl.PluralRules(i18n.language, { type: 'ordinal' }).select(
          localRecurrenceDay,
        )
        const s: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' }
        day = `${localRecurrenceDay}${s[r] ?? 'th'}`
      } catch {}
    }
    return (
      <span className="text-muted-foreground">
        {' · '}
        {day}
      </span>
    )
  }

  function applyDays(days: number[]) {
    if (!task) return
    const sched = scheduleFromDays(days)
    const wasRecurring = !!localRecursPeriod
    setLocalDays(days)
    setLocalRecursPeriod(sched.recursPeriod)
    if (sched.recurrenceDay !== undefined) setLocalRecurrenceDay(sched.recurrenceDay)
    updateRecursPeriod({
      taskId: task._id,
      recursPeriod: 'days',
      recursDays: days,
      ...(wasRecurring && { recurrenceTime: recurrenceTimeStr }),
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
    if (localDueDate) {
      setLocalDueDate(undefined)
      updateDueDate({ taskId: task._id, dueDate: undefined }).catch(() => {})
    }
  }

  function recursPopover(trigger: ReactNode) {
    if (!task) return null
    const showDays = daysOpen || localRecursPeriod === 'days'
    return (
      <Popover
        open={recursOpen}
        onOpenChange={(o) => {
          setRecursOpen(o)
          if (!o) setDaysOpen(false)
        }}
      >
        <PopoverTrigger className="-mx-1 flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50">
          {trigger}
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-[140px] p-1" side="bottom" align="start">
          {RECURS_PERIODS.map((p) =>
            p === 'days' ? (
              <button
                key={p}
                type="button"
                className="w-full"
                onClick={() => {
                  setDaysOpen(true)
                  if (localRecursPeriod)
                    setLocalDays(scheduleDays(localRecursPeriod, localRecurrenceDay, localDays))
                  else applyDays(DEFAULT_DAYS)
                }}
              >
                <span
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                    showDays ? 'text-primary-text' : 'text-foreground',
                  )}
                >
                  <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                  {t('inbox.period.days')}
                </span>
              </button>
            ) : (
              <PopoverClose
                key={p}
                className="w-full"
                onClick={() => {
                  const wasRecurring = !!localRecursPeriod
                  setLocalRecursPeriod(p)
                  updateRecursPeriod({
                    taskId: task._id,
                    recursPeriod: p,
                    ...(wasRecurring && { recurrenceTime: recurrenceTimeStr }),
                    recurrenceTzOffset: new Date().getTimezoneOffset(),
                    ...((p === 'weekly' || p === 'monthly') && {
                      recurrenceDay: localRecurrenceDay,
                    }),
                  }).catch(() => {})
                  if (localDueDate) {
                    setLocalDueDate(undefined)
                    updateDueDate({ taskId: task._id, dueDate: undefined }).catch(() => {})
                  }
                }}
              >
                <span
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
                    localRecursPeriod === p ? 'text-primary-text' : 'text-foreground',
                  )}
                >
                  <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
                  {t(`inbox.period.${p}`)}
                </span>
              </PopoverClose>
            ),
          )}
          {showDays && (
            <RecursDaysChips
              className="mt-1 border-t border-border/40 px-2 py-2"
              days={localDays}
              onChange={applyDays}
            />
          )}
          {localRecursPeriod === 'weekly' && !daysOpen && (
            <div className="mt-1 flex gap-0.5 border-t border-border/40 px-2 py-2">
              {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    setLocalRecurrenceDay(d)
                    updateRecursPeriod({
                      taskId: task._id,
                      recursPeriod: 'weekly',
                      recurrenceDay: d,
                      recurrenceTime: recurrenceTimeStr,
                      recurrenceTzOffset: new Date().getTimezoneOffset(),
                    }).catch(() => {})
                  }}
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors',
                    localRecurrenceDay === d
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted',
                  )}
                >
                  {new Intl.DateTimeFormat(i18n.language, { weekday: 'narrow' }).format(
                    new Date(2025, 0, 5 + d),
                  )}
                </button>
              ))}
            </div>
          )}
          {localRecursPeriod === 'monthly' && (
            <div className="mt-1 grid max-w-[200px] grid-cols-7 gap-0.5 border-t border-border/40 px-2 py-2">
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    setLocalRecurrenceDay(d)
                    updateRecursPeriod({
                      taskId: task._id,
                      recursPeriod: 'monthly',
                      recurrenceDay: d,
                      recurrenceTime: recurrenceTimeStr,
                      recurrenceTzOffset: new Date().getTimezoneOffset(),
                    }).catch(() => {})
                  }}
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors',
                    localRecurrenceDay === d
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted',
                  )}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
          {localRecursPeriod && !localHabit && (
            <PopoverClose className="w-full" onClick={clearRecurring}>
              <span className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10">
                <X className="h-3 w-3" strokeWidth={2.5} />
                {t('inbox.removeRecurring')}
              </span>
            </PopoverClose>
          )}
        </PopoverContent>
      </Popover>
    )
  }

  const timePicker = (
    <HabitTimePicker
      allDay={localAllDay}
      start={recurrenceTimeStr}
      durationMinutes={Math.max(15, toMinutes(recurrenceEndTimeStr) - toMinutes(recurrenceTimeStr))}
      overdue={recurrenceIsOverdue}
      onSet={setRecurTime}
      onAllDay={setRecurAllDay}
    />
  )

  function dueLabel(due: number) {
    const d = new Date(due)
    const dateStr = d.toLocaleDateString(i18next.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    if (localAllDay) return dateStr
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    // Show the from–to range once it spans more than an hour.
    if (toMinutes(dueEndTimeStr) - toMinutes(time) > 60)
      return `${dateStr}, ${time} – ${dueEndTimeStr}`
    return `${dateStr}, ${time}`
  }

  const duePopoverContent = (
    <PopoverContent className="w-[min(20rem,92vw)] p-0" side="bottom" align="start">
      <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
        {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => handleDatePreset(p)}
            className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {t(`inbox.datePreset.${p}`)}
          </button>
        ))}
      </div>
      <Calendar
        mode="single"
        disabled={dueDisabled}
        selected={selectedDay}
        defaultMonth={selectedDay ?? new Date()}
        onSelect={handleDaySelect}
        locale={locale}
        classNames={{
          root: 'w-full',
          range_start: '',
          range_middle: '',
          range_end: '',
          today: 'rounded-(--cell-radius) bg-muted text-foreground',
        }}
      />
      <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
        <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className={cn('flex items-center gap-1.5', localAllDay && 'opacity-40')}>
          <input
            type="time"
            onClick={openTimePicker}
            lang="en-GB"
            value={timeStr}
            onChange={(e) => handleDueStartChange(e.target.value)}
            className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
          />
          <span className="select-none text-xs text-muted-foreground/40">→</span>
          <input
            type="time"
            onClick={openTimePicker}
            lang="en-GB"
            value={dueEndTimeStr}
            onChange={(e) => handleDueEndChange(e.target.value)}
            className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
          />
        </div>
      </div>
      <div className="w-full px-3 pb-2">
        <button
          type="button"
          onClick={toggleAllDay}
          className={cn(
            'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
            localAllDay
              ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
              : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <Sun className="h-3 w-3 shrink-0" />
          {t('inbox.allDay')}
        </button>
      </div>
    </PopoverContent>
  )

  const savedMustDay: MustDoDay | null = task
    ? task.mustDoOn == null
      ? null
      : task.mustDoOn > localDateStr()
        ? 'tomorrow'
        : 'today'
    : null
  const mustDay = task && mustLocal?.id === (task._id as string) ? mustLocal.day : savedMustDay
  // Tapping the active day clears it; the other day moves the commitment.
  function commitMust(day: MustDoDay) {
    if (!task) return
    const next = mustDay === day ? null : day
    const prev = mustDay
    setMustLocal({ id: task._id as string, day: next })
    void mustDo(task._id, next).then(
      (ok) => !ok && setMustLocal({ id: task._id as string, day: prev }),
    )
  }

  const menuItems: DetailMenuItem[] = task
    ? [
        ...(!localHabit && !isDone
          ? [
              {
                key: 'mustToday',
                label: t('mustDo.doToday'),
                icon: <CalendarCheck />,
                inline: true,
                active: mustDay === 'today',
                onSelect: () => commitMust('today'),
              },
              {
                key: 'mustTomorrow',
                label: t('mustDo.doTomorrow'),
                icon: <CalendarClock />,
                inline: true,
                active: mustDay === 'tomorrow',
                onSelect: () => commitMust('tomorrow'),
              },
            ]
          : []),
        {
          key: 'promote',
          label: t('common.promoteToGoal'),
          icon: <TrendingUp />,
          onSelect: () => setPendingPromote(true),
        },
        {
          key: 'move',
          label: `${t('inbox.linkToGoal')}…`,
          icon: <FolderInput />,
          onSelect: () => setLinkingToGoal(true),
        },
        ...(localHabit && habitEntry
          ? [
              {
                key: 'shareCard',
                label: t('shareCard.shareProgress'),
                icon: <Share2 />,
                onSelect: () => openHabitShare(task._id, stats?.now ?? 0),
              },
            ]
          : []),
        localHabit
          ? {
              key: 'unhabit',
              label: t('inbox.stopTrackingHabit'),
              icon: <Flame />,
              // Confirm before ending a habit (loses its history).
              onSelect: () => setPendingUnhabit(true),
            }
          : {
              key: 'habit',
              label: t('inbox.trackAsHabit'),
              icon: <Flame />,
              onSelect: () => applyHabit(true),
            },
        ...(onTogglePin && !task.isHabit
          ? [
              {
                key: 'pin',
                label: t(localPinned ? 'inbox.unpin' : 'inbox.pin'),
                icon: localPinned ? <PinOff /> : <Pin />,
                inline: true,
                active: localPinned,
                onSelect: () => {
                  setLocalPinned((p) => !p)
                  onTogglePin(task._id)
                },
              },
            ]
          : []),
        ...(!localHabit && !localRecursPeriod && !isDone
          ? [
              {
                key: 'convertEvent',
                label: t('events.convertToEvent'),
                icon: <CalendarDays />,
                inline: true,
                onSelect: () => setConvertOpen(true),
              },
            ]
          : []),
        {
          key: 'delete',
          label: t('inbox.deleteEllipsis'),
          icon: <Trash2 />,
          destructive: true,
          inline: true,
          onSelect: () => {
            removeNode({ nodeId: task._id }).catch(() => toast.error('Failed to delete task'))
            onClose()
          },
        },
      ]
    : []

  return (
    <>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="ask-ai" />
      {/* Non-modal under the fullscreen session, or it would trap focus and swallow its
          controls; closing it then minimises the session back into the sheet. */}
      <Sheet
        open={!!task}
        modal={!sessionFull}
        disablePointerDismissal={sessionFull}
        onOpenChange={(open) => {
          if (open) return
          if (sessionFull) setSessionFull(null)
          else onClose()
        }}
      >
        <SheetContent
          side="right"
          className="flex w-80 flex-col p-0 sm:max-w-sm"
          finalFocus={false}
        >
          {task && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div
                ref={responseRef}
                onScroll={onResponseScroll}
                className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pt-1.5 pb-4"
              >
                {/* Top bar: kind, ⋯ menu (close button sits to its right) */}
                <div className="flex h-8 shrink-0 items-center justify-between gap-2 pr-8">
                  <DetailKindLabel>
                    {localHabit ? t('inbox.habit') : t('inbox.taskLabel')}
                  </DetailKindLabel>
                  <DetailMenu
                    confirmTitle={t('common.deleteTaskTitle')}
                    confirmDescription={t('common.deleteTaskDesc', { title: task.title })}
                    items={menuItems}
                  />
                </div>

                {/* Title + description (+ tags for habits; tasks show a Tags row) */}
                <div className="flex shrink-0 flex-col gap-1.5">
                  {(() => {
                    const parsed = parseTaskFields(localFullTitle || task.title)
                    const cleanDesc = parsed.description
                    const tags = parsed.tags
                    return (
                      <>
                        {renaming ? (
                          <textarea
                            ref={renameRef}
                            rows={1}
                            value={renameValue}
                            onChange={(e) => {
                              const val = e.target.value
                              setRenameValue(val)
                              e.target.style.height = 'auto'
                              e.target.style.height = `${e.target.scrollHeight}px`
                              {
                                const p = parseTaskFields(val)
                                setIsUrgent(p.effort === 'high')
                                setIsSuper(p.superUrgent)
                              }
                              if (renameTimerRef.current) clearTimeout(renameTimerRef.current)
                              renameTimerRef.current = setTimeout(() => {
                                const trimmed = val.trim()
                                if (!trimmed) return
                                const {
                                  title,
                                  description,
                                  tags,
                                  effort: parsedEffort,
                                  superUrgent: parsedSuper,
                                } = parseTaskFields(trimmed)
                                setLocalFullTitle(trimmed)
                                setLocalTitle(title)
                                void titleHint.track(
                                  renameFull({ nodeId: task._id, title, description, tags }),
                                )
                                const effectiveEffort = parsedEffort ?? 'low'
                                setIsUrgent(effectiveEffort === 'high')
                                setIsSuper(parsedSuper)
                                applyEffort(effectiveEffort, parsedSuper)
                              }, 500)
                            }}
                            onBlur={() => {
                              if (renameTimerRef.current) {
                                clearTimeout(renameTimerRef.current)
                                renameTimerRef.current = null
                              }
                              const trimmed = renameValue.trim()
                              if (trimmed) {
                                const {
                                  title,
                                  description,
                                  tags,
                                  effort: parsedEffort,
                                  superUrgent: parsedSuper,
                                } = parseTaskFields(trimmed)
                                const changed = trimmed !== (localFullTitle || task.title)
                                setLocalFullTitle(trimmed)
                                setLocalTitle(title)
                                const save = renameFull({
                                  nodeId: task._id,
                                  title,
                                  description,
                                  tags,
                                })
                                if (changed) void titleHint.track(save)
                                else save.catch(() => {})
                                const effectiveEffort = parsedEffort ?? 'low'
                                setIsUrgent(effectiveEffort === 'high')
                                setIsSuper(parsedSuper)
                                applyEffort(effectiveEffort, parsedSuper)
                              }
                              setRenaming(false)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                e.currentTarget.blur()
                              }
                              if (e.key === 'Escape') {
                                if (renameTimerRef.current) {
                                  clearTimeout(renameTimerRef.current)
                                  renameTimerRef.current = null
                                }
                                setRenaming(false)
                              }
                            }}
                            className={cn(
                              'min-h-9 w-full resize-none overflow-y-auto bg-transparent text-2xl font-bold leading-tight outline-none [field-sizing:content]',
                              titleDone ? 'text-foreground/50 line-through' : 'text-foreground',
                            )}
                          />
                        ) : (
                          <p
                            className={cn(
                              'text-2xl font-bold leading-tight',
                              titleDone ? 'text-foreground/50 line-through' : 'text-foreground',
                            )}
                          >
                            {/* line-through stays on the <p> (propagates onto the
                              child text); the inner span adds the hover underline
                              so both show on hover, and only line-through shows
                              while editing. */}
                            <span
                              className="cursor-text hover:underline decoration-dashed underline-offset-4"
                              onClick={(e) => {
                                tapPositionRef.current = { x: e.clientX, y: e.clientY }
                                setRenameValue(localFullTitle || task.title)
                                setRenaming(true)
                              }}
                            >
                              {localTitle}
                            </span>
                          </p>
                        )}
                        <SavedHint status={titleHint.status} />
                        {cleanDesc && (
                          <MarkdownContent
                            text={cleanDesc}
                            className="!text-muted-foreground/70 prose-p:!my-0"
                            linkChips
                          />
                        )}
                        {localHabit && tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {tags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary-text/70"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>

                {localHabit && habitEntry?.quit && (
                  <QuitHero habitId={task._id} quit={habitEntry.quit} />
                )}
                {/* A number habit's amount is the main thing to log, so it leads. */}
                {localHabit && (
                  <NumberHabitStrip
                    habitId={task._id}
                    className="rounded-xl border border-border/60 px-3 py-2.5"
                  />
                )}
                {/* Primary action. A past/future calendar occurrence gets a
                    day-labelled CTA instead; today falls through to the live one. */}
                {occ && occ.kind === 'past' && isFastHabit ? (
                  <span className="rounded-xl border border-border/40 bg-muted/20 px-3 py-2.5 text-center text-xs text-muted-foreground">
                    {t('fasting.pastDay')}
                  </span>
                ) : occ && occ.kind === 'past' ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (isTooFarBack(occ.ds)) return void toast(t('backdate.tooFarBack'))
                      const nextDone = !occ.done
                      if (nextDone) playDoneSound()
                      setCompletionForDate({
                        taskId: task._id,
                        date: occ.ds,
                        tzOffset: new Date().getTimezoneOffset(),
                        done: nextDone,
                      }).catch(() => toast.error(t('inbox.occurrenceError')))
                    }}
                    aria-disabled={isTooFarBack(occ.ds) || undefined}
                    className={cn(
                      'flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border py-2.5 text-sm font-bold transition-colors',
                      isTooFarBack(occ.ds) && 'opacity-50',
                      occ.done
                        ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
                        : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <TaskStatusIcon status={occ.done ? 'done' : 'pending'} size={16} />
                    {occ.done
                      ? t('inbox.occurrenceDone', { date: occ.label })
                      : t('inbox.occurrenceComplete', { date: occ.label })}
                  </button>
                ) : occ && occ.kind === 'future' ? (
                  <span className="flex items-center justify-center gap-1.5 rounded-xl border border-border/40 bg-muted/20 py-2.5 text-sm font-medium text-muted-foreground/60">
                    <TaskStatusIcon status="pending" size={16} />
                    {t('inbox.occurrenceFuture', { date: occ.label })}
                  </span>
                ) : isFastHabit ? (
                  <button
                    type="button"
                    onClick={() => void fastActions.toggle(task._id)}
                    className="flex cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    {fastProgress?.fast ? (
                      <Square className="size-3.5 fill-current" />
                    ) : (
                      <Play className="size-3.5 fill-current" />
                    )}
                    {t(fastProgress?.fast ? 'fasting.end' : 'fasting.start')}
                  </button>
                ) : (
                  <DetailPrimaryAction
                    kind={localHabit ? 'habit' : 'task'}
                    period={localRecursPeriod}
                    done={isDone}
                    onClick={toggleComplete}
                    aside={
                      stats && (
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">
                          <StreakFlame /> <b className="font-bold text-foreground">{stats.now}</b> ·{' '}
                          {t('inbox.statsBest')}{' '}
                          <b className="font-bold text-foreground">{stats.best}</b>
                          {sheetStrength && sheetStrength.strength > 0 && (
                            <>
                              {' · '}
                              <StrengthChip
                                value={sheetStrength.strength}
                                className="font-bold text-foreground"
                              />
                            </>
                          )}
                        </span>
                      )
                    }
                  />
                )}
                {/* Late-night habits are often remembered the next morning. */}
                {localHabit &&
                  !occ &&
                  !isFastHabit &&
                  (task._creationTime ?? 0) < Date.now() - 86_400_000 &&
                  dueOn(task.recursPeriod, task.recurrenceDay, yesterdayDate(), task.recursDays) &&
                  (() => {
                    const doneYesterday = doneOnDay(habitCompletions, yesterdayKey())
                    return (
                      <button
                        type="button"
                        onClick={() => void markYesterday(task._id, task.title, !doneYesterday)}
                        className="-mt-2 flex cursor-pointer items-center justify-center gap-1.5 self-center rounded-full px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <History className="size-3.5" />
                        {doneYesterday
                          ? t('habitYesterday.doneYesterday')
                          : t('habitYesterday.forgot')}
                      </button>
                    )
                  })()}

                {/* Properties */}
                <DetailRows>
                  {localHabit ? (
                    <>
                      <DetailRow label={t('inbox.rowSchedule')}>
                        {recursPopover(
                          <>
                            <RefreshCw className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                            {periodLabel(t, i18n.language, localRecursPeriod, localDays)}
                            {recursDaySuffix()}
                          </>,
                        )}
                        {timePicker}
                      </DetailRow>
                      <DetailRow label={t('inbox.icon')}>
                        <Popover>
                          <PopoverTrigger className="-mx-1 flex cursor-pointer items-center rounded px-1 py-0.5 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50">
                            <HabitIcon
                              habit={{
                                icon: localIcon,
                                title: localTitle,
                                category: localCategory,
                              }}
                              color={fill(habitColor(localCategory))}
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-0" side="bottom" align="start">
                            <HabitIconPicker
                              value={iconName}
                              color={fill(habitColor(localCategory))}
                              onPick={(icon) => {
                                setLocalIcon(icon)
                                updateIcon({ taskId: task._id, icon }).catch(() => {})
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </DetailRow>
                      <DetailRow label={t('dotStyle.row')}>
                        <DotStyleButton
                          habitId={task._id}
                          habit={{ icon: localIcon, title: localTitle, category: localCategory }}
                        />
                      </DetailRow>
                      <DetailRow label={t('inbox.anchor')}>
                        <Popover>
                          <PopoverTrigger
                            className={cn(
                              '-mx-1 flex min-w-0 cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-left outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50',
                              !anchorLabel && 'text-muted-foreground',
                            )}
                          >
                            <span className="truncate">{anchorLabel ?? t('inbox.anchorNone')}</span>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 p-0" side="bottom" align="start">
                            <HabitAnchorPicker
                              taskId={task._id}
                              value={localAnchor}
                              onChange={setLocalAnchor}
                            />
                          </PopoverContent>
                        </Popover>
                      </DetailRow>
                      <DetailRow label={t('inbox.category')}>
                        <Popover>
                          <PopoverTrigger
                            className={cn(
                              '-mx-1 flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 font-semibold outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50',
                              !hasCategory && 'font-normal text-muted-foreground',
                            )}
                            style={
                              hasCategory
                                ? { color: categoryTextColor(habitColor(localCategory)) }
                                : undefined
                            }
                          >
                            {hasCategory ? (
                              <>
                                <span
                                  className="h-1.5 w-1.5 rounded-full"
                                  style={{ background: fill(habitColor(localCategory)) }}
                                />
                                {t(`goals.cat.${localCategory.toLowerCase()}`)}
                              </>
                            ) : (
                              t('inbox.none')
                            )}
                          </PopoverTrigger>
                          <PopoverContent className="w-40 p-1" side="bottom" align="start">
                            {POPULAR_HABIT_CATEGORIES.map((c) => (
                              <PopoverClose
                                key={c}
                                onClick={() => {
                                  setLocalCategory(c)
                                  updateCategory({ taskId: task._id, category: c }).catch(() => {})
                                }}
                                className="flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors hover:bg-muted"
                              >
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={{ background: fill(habitColor(c)) }}
                                />
                                {t(`goals.cat.${c}`)}
                              </PopoverClose>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </DetailRow>
                    </>
                  ) : (
                    <>
                      <DetailRow label={t('inbox.rowDue')}>
                        <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                          <PopoverTrigger
                            className={cn(
                              '-mx-1 cursor-pointer rounded px-1 py-0.5 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50',
                              !localDueDate && 'text-muted-foreground',
                              localDueDate &&
                                isDueTodayOrOverdue(localDueDate) &&
                                'font-semibold text-red-600 dark:text-red-400',
                            )}
                          >
                            {localDueDate && isTaskOverdue(localDueDate, localAllDay) && (
                              <>{t('inbox.overdue')} · </>
                            )}
                            {localDueDate ? (
                              dueLabel(localDueDate)
                            ) : (
                              <span className="flex items-center gap-1">
                                {t('inbox.addDueDate')}
                                <Plus className="h-3 w-3" />
                              </span>
                            )}
                          </PopoverTrigger>
                          {duePopoverContent}
                        </Popover>
                        {localDueDate && (
                          <button
                            type="button"
                            aria-label={t('common.clear', { defaultValue: 'Clear' })}
                            onClick={() => {
                              setLocalDueDate(undefined)
                              updateDueDate({ taskId: task._id, dueDate: undefined }).catch(
                                () => {},
                              )
                            }}
                            className="ml-auto flex cursor-pointer items-center rounded p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                          >
                            <X className="h-3 w-3" strokeWidth={2.5} />
                          </button>
                        )}
                      </DetailRow>
                      <TaskLocationRow taskId={task._id} location={task.location} />
                      <DetailRow label={t('inbox.rowPriority')} onClick={toggleUrgent}>
                        {isUrgent ? (
                          <span className="flex items-center gap-1.5 font-semibold text-red-600 dark:text-red-400">
                            <UrgentFlag className="h-3 w-3" superUrgent={isSuper} />
                            {isSuper ? t('filter.prioritySuperUrgent') : t('filter.priorityUrgent')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {t('filter.priorityNormal')}
                          </span>
                        )}
                      </DetailRow>
                      <DetailRow label={t('filter.status')} onClick={toggleInProgress}>
                        <span className="flex items-center gap-1.5">
                          <TaskStatusIcon
                            status={isDone ? 'done' : inProgress ? 'in_progress' : 'pending'}
                            size={14}
                          />
                          {isDone
                            ? t('filter.statusDone')
                            : inProgress
                              ? t('goals.inProgress')
                              : t('inbox.notStarted')}
                        </span>
                      </DetailRow>
                      <DetailRow label={t('inbox.rowRepeats')}>
                        {recursPopover(
                          localRecursPeriod ? (
                            <>
                              <RefreshCw className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                              {periodLabel(t, i18n.language, localRecursPeriod, localDays)}
                              {recursDaySuffix()}
                            </>
                          ) : (
                            <span className="text-muted-foreground">{t('inbox.repeatsNever')}</span>
                          ),
                        )}
                        {localRecursPeriod && timePicker}
                      </DetailRow>
                    </>
                  )}
                  {!localHabit && taskTagList.length > 0 && (
                    <DetailRow label={t('inbox.rowTags')}>
                      {taskTagList.map((tag) => (
                        <span key={tag} className="text-primary-text">
                          #{tag}
                        </span>
                      ))}
                    </DetailRow>
                  )}
                </DetailRows>

                {/* Skip today ("not today") — habits on the Today screen only. Optional
                    reason, never required; the habit stays completable here in the inbox. */}
                {onSkipToday && localHabit && !occ && !isDone && (
                  <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto">
                    <span className="mr-1 shrink-0 text-xs font-medium text-muted-foreground">
                      {t('today.skipNotTodayLabel')}
                    </span>
                    {HABIT_SKIP_REASONS.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => onSkipToday(task._id, r)}
                        className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-border/60 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <SkipReasonIcon reason={r} />
                        {t(`today.skipReason.${r}`)}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => onSkipToday(task._id)}
                      className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-border/60 bg-muted/30 px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {t('today.skip')}
                    </button>
                  </div>
                )}

                {localHabit && (
                  <SessionPlanSection
                    habitId={task._id}
                    doneToday={isDone}
                    coachName={coachName}
                    onComplete={(id) => {
                      setLocallyDone(true)
                      onComplete(id)
                    }}
                  />
                )}

                {/* History */}
                {localHabit && stats && (
                  <div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {t('inbox.history')}
                      </span>
                      {createdAt && (
                        <span className="font-mono text-[11px] text-muted-foreground/70">
                          {t('inbox.historySince', {
                            date: new Date(createdAt).toLocaleDateString(i18n.language, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }),
                          })}
                        </span>
                      )}
                    </div>
                    {/* The whole history block opens the habit's stats page. */}
                    <div
                      role="link"
                      tabIndex={0}
                      aria-label={t('habitStats.viewStats')}
                      title={t('habitStats.viewStats')}
                      onClick={openStats}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openStats()
                        }
                      }}
                      className="group/stats -mx-2 block w-[calc(100%+1rem)] cursor-pointer rounded-xl px-2 py-1.5 text-left outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/60"
                    >
                      <HabitStatsLine
                        stats={stats}
                        toggle={
                          <ChevronRight className="ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform group-hover/stats:translate-x-0.5 group-hover/stats:text-foreground" />
                        }
                      />
                      <HabitStrengthPanel
                        habitId={task._id}
                        color={fill(habitColor(task.category))}
                        className="mt-3"
                      />
                      <div className="mt-3">
                        <HabitDotGrid
                          completions={habitCompletions}
                          color={habitColor(task.category)}
                          period={recurrencePeriod ?? task.recursPeriod}
                          recursDays={localDays}
                          createdAt={createdAt}
                          habitId={task._id}
                          sweepOnMountMs={450}
                        />
                      </div>
                    </div>
                    {habitEntry0 &&
                      !isFastHabit &&
                      !habitPhotos?.has(logicalDayStr(Date.now())) && (
                        <button
                          type="button"
                          onClick={() => setPhotoOpen(true)}
                          className="mt-1 flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                        >
                          <Camera className="size-3.5" />
                          {t('dotPhoto.addLink')}
                        </button>
                      )}
                  </div>
                )}

                {localHabit && <MeasureSettings habitId={task._id} />}
                {localHabit && <LevelUpSection habitId={task._id} />}
                {localHabit && <AutoCheckRow habitId={task._id} />}
                {localHabit && <QuitToggleRow habitId={task._id} />}
                {localHabit && (
                  <QuickLogLinkRow habitId={task._id} isNumber={!!habitEntry?.measure} />
                )}

                {/* Delete sits last, away from the controls used every day. */}
                <DetailActions
                  confirmTitle={t('common.deleteTaskTitle')}
                  confirmDescription={t('common.deleteTaskDesc', { title: task.title })}
                  items={menuItems}
                />

                <AskCoachThread chat={chat} voice={coachVoice} chips={askChips} />
              </div>

              <AskCoachComposer
                subject="task"
                chips={askChips}
                chat={chat}
                coachName={coachName}
                onSend={handleAsk}
                resetKey={task?._id}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {task && habitEntry0 && photoOpen && (
        <PhotoCheckinSheet
          habitId={task._id}
          title={task.title}
          category={task.category}
          date={logicalDayStr(Date.now())}
          ask={habitEntry0.photoAsk}
          checkedIn={false}
          discover={habitEntry0.dot?.kind === 'photo' ? undefined : 'link'}
          onClose={() => setPhotoOpen(false)}
        />
      )}

      {task && (
        <ConvertToEventDialog
          taskId={task._id}
          title={task.title}
          dueDate={localDueDate ?? task.dueDate}
          open={convertOpen}
          onOpenChange={setConvertOpen}
          onConverted={onClose}
        />
      )}

      {task && (
        <AlertDialog
          open={pendingPromote}
          onOpenChange={(open) => !promoting && setPendingPromote(open)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.promoteToGoal')}</AlertDialogTitle>
              <AlertDialogDescription>
                "{task.title}" will become a standalone goal with an AI-generated plan. The inbox
                task will be removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={promoting}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                disabled={promoting}
                onClick={async (e) => {
                  e.preventDefault()
                  setPromoting(true)
                  try {
                    const { goalId, title } = await promoteInboxTaskToGoal({ taskId: task._id })
                    onClose()
                    navigate({
                      to: '/goals/$goalId',
                      params: { goalId: goalUrl(title, goalId).replace('/goals/', '') },
                    })
                  } catch (err) {
                    if (!handleUsageLimit(err))
                      toast.error('Failed to promote task. Please try again.')
                  } finally {
                    setPromoting(false)
                    setPendingPromote(false)
                  }
                }}
              >
                {promoting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  t('common.promoteToGoal')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {task && (
        <AlertDialog open={pendingUnhabit} onOpenChange={setPendingUnhabit}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('inbox.endHabitTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('inbox.endHabitDesc')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => {
                  setPendingUnhabit(false)
                  applyHabit(false)
                }}
              >
                {t('inbox.endHabit')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {task && (
        <GoalPickerSheet
          taskId={linkingToGoal ? task._id : null}
          onDone={() => setLinkingToGoal(false)}
          onCancel={() => setLinkingToGoal(false)}
        />
      )}
    </>
  )
}
